/**
 * Custom hook for generating language URLs for job detail pages
 */

import { useMemo } from 'react';
import { BASE_URL, LOCALIZED_PATHS } from '../../../../../utils/constants/url-constants';
import type { FinalJob } from '../../../../../lambda/types';

export interface LanguageUrls {
  current: string;
  alternates: Array<{ href: string; hrefLang: string }>;
  all: Array<{ locale: 'en' | 'fi' | 'sv'; url: string }>;
}

export interface UseLanguageUrlsOptions {
  canonicalSource: FinalJob | null | undefined;
  canonicalLocale: 'en' | 'fi' | 'sv';
}

/**
 * Generate URLs for all languages using current host
 */
export function useLanguageUrls({ canonicalSource, canonicalLocale }: UseLanguageUrlsOptions): LanguageUrls | null {
  return useMemo(() => {
    if (!canonicalSource) return null;

    const supportedLocales: Array<'en' | 'fi' | 'sv'> = ['en', 'fi', 'sv'];

    // Helper to sanitize strings for URLs
    const sanitizeForUrl = (str: string | undefined): string => {
      if (!str || typeof str !== 'string') return '';
      try {
        return str
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLowerCase()
          .trim()
          .replace(/[^a-z0-9\s-]/g, '')
          .replace(/\s+/g, '-')
          .replace(/-+/g, '-')
          .replace(/^-+|-+$/g, '');
      } catch (e) {
        return '';
      }
    };

    // Helper to generate SEO slug for a locale
    const generateSeoSlug = (locale: 'en' | 'fi' | 'sv'): string => {
      const seoElements: string[] = [];
      const title = canonicalSource[`title_${locale}`] || canonicalSource[`normalisedTitle_${locale}`] || canonicalSource.title_en || canonicalSource.title_fi || canonicalSource.title_sv || '';
      const sanitizedTitle = sanitizeForUrl(title);
      if (sanitizedTitle) seoElements.push(sanitizedTitle);
      const company = canonicalSource.companyId || '';
      const sanitizedCompany = sanitizeForUrl(company);
      if (sanitizedCompany) seoElements.push(sanitizedCompany);
      const location = canonicalSource[`location_${locale}`]?.[0] || canonicalSource.location_en?.[0] || canonicalSource.location_fi?.[0] || canonicalSource.location_sv?.[0] || '';
      const sanitizedLocation = sanitizeForUrl(location);
      if (sanitizedLocation) seoElements.push(sanitizedLocation);
      const remoteness = canonicalSource[`remoteness_${locale}`] || canonicalSource.remoteness_en || canonicalSource.remoteness_fi || canonicalSource.remoteness_sv || '';
      const sanitizedRemoteness = sanitizeForUrl(remoteness);
      if (sanitizedRemoteness) seoElements.push(sanitizedRemoteness);
      return seoElements.join('-');
    };

    // Generate relative paths for all locales with locale prefix (Link component recognizes and uses as-is)
    const allUrls = supportedLocales.map(locale => {
      const seoSlug = generateSeoSlug(locale);
      // Include locale prefix - Link component will recognize it and use as-is
      const path = `/${locale}/${LOCALIZED_PATHS[locale].job}/${canonicalSource.objectID}/${canonicalSource.companyId || ''}${seoSlug ? `/${seoSlug}` : ''}`;
      return {
        locale,
        url: path
      };
    });

    // Get current URL for schema (absolute)
    const getCurrentAbsoluteUrl = () => {
      if (typeof window !== 'undefined') {
        return `${window.location.origin}${allUrls.find(u => u.locale === canonicalLocale)?.url || ''}`;
      }
      const currentPath = allUrls.find(u => u.locale === canonicalLocale)?.url || '';
      return `${BASE_URL}${currentPath}`;
    };

    return {
      current: getCurrentAbsoluteUrl(),
      alternates: allUrls.filter(u => u.locale !== canonicalLocale).map(u => ({
        href: typeof window !== 'undefined' ? `${window.location.origin}${u.url}` : `${BASE_URL}${u.url}`,
        hrefLang: u.locale
      })),
      all: allUrls
    };
  }, [canonicalSource, canonicalLocale]);
}
